/**
 * FullDetailsTemplate - Comprehensive photo display with all metadata
 *
 * Displays image along with description, EXIF data, and engagement metrics.
 * This is the "maximal" template showing all available information.
 *
 * Feature: 649-critique-feedback-options - Updated rating display
 *
 * @module PhotoItem2/templates/FullDetailsTemplate
 */

import React from 'react';
import { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { cn } from '@/lib/utils';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { PhotoMetadata } from '../../PhotoMetadata/PhotoMetadata';
import { CompactRatingDisplay } from '../../composed/CompactRatingDisplay';
import { SelectionIndicator } from '@/components/atoms';

/**
 * FullDetailsTemplate component
 *
 * Features:
 * - Image display with automatic resolution selection based on container size
 * - Description text (when available)
 * - Complete EXIF metadata grid with "n/a" for missing fields
 * - Critique count and ratings display
 * - Selection indicators (ring + triangle wedge)
 * - Uses AdaptivePhoto for optimal image loading
 */
export const FullDetailsTemplate: React.FC<PhotoItem2TemplateProps> = ({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick: _onCritiqueClick, // Filter out non-DOM prop
  className = '',
  ...restProps
}) => {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead
  void _onCritiqueClick; // Filter out - not a valid DOM prop

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to div
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative flex flex-col rounded-lg overflow-hidden bg-surface-1 w-full h-full select-none',
        selectable && 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all',
        isSelected && 'ring-4 ring-primary',
        className
      )}
      {...restProps}
    >
      {/* Selection indicator */}
      <SelectionIndicator visible={isSelected} size="md" className="z-10" />

      {/* Image - flex-1 allows it to take available space */}
      <div className="w-full flex-1 min-h-0">
        <AdaptivePhoto
          photo={data.photoData}
          alt={data.photoData.description || ''}
          className="w-full h-full bg-surface-2 p-0.5"
        />
      </div>

      {/* Metadata section - compact and fixed size */}
      <div className="px-2 py-1.5 space-y-1 flex-shrink-0">
        {/* Description */}
        {data.photoData.description && (
          <p className="text-sm text-foreground leading-snug line-clamp-2">{data.photoData.description}</p>
        )}

        {/* EXIF Data - using PhotoMetadata component */}
        <PhotoMetadata photoData={data.photoData} variant="full" />

        {/* Engagement metrics */}
        <div className="flex items-center gap-2 text-sm text-muted border-t border-border pt-1">
          <div className="text-sm">
            <span className="font-medium">Critiques:</span> {data.critiqueCount}
          </div>
          {/* Rating display - shows average for photographers, own rating for critics */}
          <CompactRatingDisplay
            photoId={data.photoData.id}
            size={14}
            variant="inline"
          />
        </div>
      </div>
    </div>
  );
};

FullDetailsTemplate.displayName = 'FullDetailsTemplate';
